import { z } from "zod";

export interface CreateIpAllowlistValues {
  readonly cidr: string;
  readonly description: string;
}

function isIpv4Address(value: string) {
  const octets = value.split(".");
  return octets.length === 4 && octets.every((octet) => (
    /^\d{1,3}$/.test(octet) && Number(octet) <= 255
  ));
}

function ipv6GroupCount(value: string): number | null {
  if (!value) return 0;
  const groups = value.split(":");
  let count = 0;
  for (const [index, group] of groups.entries()) {
    if (/^[0-9A-Fa-f]{1,4}$/.test(group)) {
      count += 1;
      continue;
    }
    if (index === groups.length - 1 && isIpv4Address(group)) {
      count += 2;
      continue;
    }
    return null;
  }
  return count;
}

function isIpv6Address(value: string) {
  if (!value.includes(":") || value.includes("%") || !/^[0-9A-Fa-f:.]+$/.test(value)) return false;
  const contraction = value.indexOf("::");
  if (contraction !== value.lastIndexOf("::")) return false;
  if (contraction < 0) return ipv6GroupCount(value) === 8;
  const left = ipv6GroupCount(value.slice(0, contraction));
  const right = ipv6GroupCount(value.slice(contraction + 2));
  return left !== null && right !== null && left + right < 8;
}

export function isValidNumericCidr(value: string) {
  const trimmed = value.trim();
  const separator = trimmed.lastIndexOf("/");
  if (separator <= 0 || separator !== trimmed.indexOf("/")) return false;
  const address = trimmed.slice(0, separator);
  const prefixText = trimmed.slice(separator + 1);
  if (!/^\d{1,3}$/.test(prefixText)) return false;
  const prefix = Number(prefixText);
  if (address.includes(":")) return isIpv6Address(address) && prefix <= 128;
  return isIpv4Address(address) && prefix <= 32;
}

export const createIpAllowlistSchema = z.object({
  cidr: z.string()
    .trim()
    .min(1, "CIDR을 입력해 주세요.")
    .max(64, "CIDR은 64자까지 입력할 수 있습니다.")
    .refine(isValidNumericCidr, "IPv4 또는 IPv6 숫자 주소와 올바른 prefix를 입력해 주세요."),
  description: z.string().trim().max(255, "설명은 255자까지 입력할 수 있습니다."),
});

export function parseIpAllowlistId(value: string | null) {
  if (!value || !/^\d+$/.test(value)) return undefined;
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : undefined;
}
